import React from 'react';

import {
  fallbackText,
  HelpTypes,
  IAdminPharmacistDetails,
  IAdminProfessionalDetails,
  ServiceTypes,
} from '@pharmaplan/common';

import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import strings from '../../../../../localization';

import {
  handleBooleanValue,
  handleValue,
} from '../../Pharmacy/PharmacyViewProfile/helper';
import {
  OutcomeModalTypes,
  TypeOfSoftwareExpertise,
  preferredDaysMap,
} from '../../../../../helpers/Constants';
import SoftwareExpertise from '../../../../SelfService/SoftwareExpertise';
import { setDialog } from '../../../../../reducers/dialogReducer';
import OutcomeModal from '../../../../Modals/OutcomeModal';
import { store } from '../../../../../store';
import { languageLevelMap } from '../../../../Profile/ProfileForm/Forms';

export const professionalSelfServiceKeys = {
  configuration: 'configuration',
  billing: 'billing',
  incorporated: 'incorporated',
  licenseKey: 'licenseNumber',
  graduationYear: 'graduationYear',
  yearsOfExperience: 'yearsOfExperience',
};

export const showSuccess = (
  dispatch: typeof store.dispatch,
  message: string,
) => {
  dispatch(
    setDialog({
      heading: {
        title: '',
      },
      Component: (
        <OutcomeModal type={OutcomeModalTypes.justSuccess} message={message} />
      ),
      showCloseButton: true,
    }),
  );
};

export const showError = (dispatch: typeof store.dispatch, message: string) => {
  dispatch(
    setDialog({
      heading: {
        title: '',
      },
      Component: (
        <OutcomeModal type={OutcomeModalTypes.error} message={message} />
      ),
      showCloseButton: true,
    }),
  );
};

export const handlePreferredDays = (preferredWorkingDays: string) =>
  preferredWorkingDays
    ?.split(',')
    .map((item) =>
      preferredDaysMap()[parseInt(item, 10)])
    .join('; ');

export const pharmacistProfileConfig = (
  details: IAdminPharmacistDetails | IAdminProfessionalDetails,
  language: string,
  pharmacistCapacity: string,
  successAction: string,
  selectedPreferences: string,
  fromPendingActivations: boolean,
) => {
  const isSelfService = ServiceHandler.getService() === ServiceTypes.self;

  const {
    phone,
    firstName,
    lastName,
    licenseNumber,
    graduationYear,
    yearsOfExperience,
    otherLanguages,
    city,
    province,
    address,
    postalCode,
    mobile,
    availabilityReminder,
    preferredWorkingDays,
    preferredReplacementDuration,
    selectedSoftwares,
    frenchLevel,
    englishLevel,
    incorporated,
    userType,
  } = details ?? {};
  const isStudent = `${userType}` === HelpTypes.students;

  const {
    branchNumber,
    institutionNumber,
    accountNumber,
    corporateProvince,
    corporateCity,
    corporateCompanyName,
    corporatePhone,
    corporatePostalCode,
    corporateStreetAddress,
    tps,
    tvq,
  } = (details as IAdminProfessionalDetails) ?? {};

  const incorporatedInfo = [
    {
      key: 1,
      label: strings.companyName,
      value: corporateCompanyName,
    },
    {
      key: 2,
      label: strings.streetAddress,
      value: corporateStreetAddress,
    },
    {
      key: 3,
      label: strings.city,
      value: corporateCity,
    },
    {
      key: 4,
      label: strings.province,
      value: corporateProvince,
    },
    {
      key: 5,
      label: strings.postalCode,
      value: corporatePostalCode,
    },
    {
      key: 6,
      label: strings.telephoneNumber,
      value: corporatePhone,
    },
    {
      key: 7,
      label: strings.tpsNumber,
      value: tps ?? fallbackText,
    },
    {
      key: 8,
      label: strings.tvqNumber,
      value: tvq ?? fallbackText,
    },
  ];

  const nonIncorporatedInfo = [
    {
      key: 9,
      label: strings.tpsNumber,
      value: tps ?? fallbackText,
    },
    {
      key: 10,
      label: strings.tvqNumber,
      value: tvq ?? fallbackText,
    },
  ];

  const preferencesConfig = [
    {
      key: 0,
      label: strings.preferredWorkingDays,
      value: handleValue(handlePreferredDays(preferredWorkingDays as string)),
    },
    {
      key: 1,
      label: strings.preferredReplacementDuration,
      value: handleValue(preferredReplacementDuration),
    },
  ];

  let infoForm: any[] = [];

  if (incorporated) {
    infoForm = incorporatedInfo;
  } else {
    infoForm = nonIncorporatedInfo;
  }

  if (!isSelfService) {
    preferencesConfig.push({
      key: 2,
      label: strings.availabilityReminder,
      value: handleBooleanValue(availabilityReminder),
    });
  }

  let professionalArr = [
    {
      key: professionalSelfServiceKeys.licenseKey,
      label: strings.licenseNumber,
      value: handleValue(licenseNumber),
    },
    {
      key: 1,
      label: isSelfService
        ? strings.professionalCapacity
        : strings.pharmacistCapacity,
      value: handleValue(pharmacistCapacity),
    },
    {
      key: professionalSelfServiceKeys.graduationYear,
      label: strings.graduationYear,
      value: handleValue(graduationYear),
    },
    {
      key: professionalSelfServiceKeys.yearsOfExperience,
      label: strings.yearsOfExperience,
      value: handleValue(yearsOfExperience),
    },
    {
      key: 4,
      label: strings.englishLevel,
      value: languageLevelMap()[parseInt(englishLevel as string, 10)],
    },
    {
      key: 5,
      label: strings.frenchLevel,
      value: languageLevelMap()[parseInt(frenchLevel as string, 10)],
    },
    {
      key: 6,
      label: strings.otherLanguage,
      value: handleValue(otherLanguages),
    },
    {
      key: 7,
      label: strings.areYouIncorporated,
      value: handleBooleanValue(incorporated),
    },
  ];

  const keysToRemove = [
    professionalSelfServiceKeys.licenseKey,
    professionalSelfServiceKeys.yearsOfExperience,
    professionalSelfServiceKeys.graduationYear,
  ];

  if (isStudent) {
    professionalArr = professionalArr.filter(
      (item) =>
        !keysToRemove.includes(item.key as string),
    );
  }

  const arr = [
    {
      key: 0,
      title: strings.general,
      keepExpanded: true,
      cardContent: [
        {
          key: 1,
          label: strings.firstName,
          value: handleValue(firstName),
        },
        {
          key: 2,
          label: strings.lastName,
          value: handleValue(lastName),
        },
        {
          key: 3,
          label: strings.preferredLanguage,
          value: handleValue(language),
        },
        {
          key: 4,
          label: strings.contactPreferences,
          value: handleValue(selectedPreferences),
        },
      ],
    },
    {
      key: 1,
      title: strings.professionalInformation,
      cardContent: professionalArr,
    },
    {
      key: professionalSelfServiceKeys.incorporated,
      title: strings.incorporated,
      cardContent: infoForm,
    },
    {
      key: professionalSelfServiceKeys.billing,
      title: strings.billing,
      cardContent: [
        {
          key: 1,
          label: strings.institutionNo,
          value: handleValue(institutionNumber),
        },
        {
          key: 2,
          label: strings.branchNo,
          value: handleValue(branchNumber),
        },
        {
          key: 3,
          label: strings.accountNo,
          value: accountNumber
            ? handleValue('*'.repeat(5).concat(accountNumber))
            : fallbackText,
        },
      ],
    },
    {
      key: 2,
      title: strings.softwareFamiliarity,
      customComponent: (
        <SoftwareExpertise
          successAction={successAction}
          softwareDetails={selectedSoftwares}
          layout={TypeOfSoftwareExpertise.softwareFamilarity}
        />
      ),
    },
    {
      key: 3,
      title: strings.contactDetails,
      cardContent: [
        {
          key: 0,
          label: strings.address,
          value: handleValue(address),
        },
        {
          key: 1,
          label: strings.city,
          value: handleValue(city),
        },
        {
          key: 2,
          label: strings.province,
          value: handleValue(province),
        },
        {
          key: 3,
          label: strings.postalCode,
          value: handleValue(postalCode),
        },
        {
          key: 4,
          label: strings.homePhone,
          value: handleValue(phone),
        },
        {
          key: 5,
          label: strings.mobile,
          value: handleValue(mobile),
        },
      ],
    },
    {
      key: 4,
      title: strings.preferences,
      cardContent: preferencesConfig,
    },
  ];

  if (!isSelfService) {
    return arr.filter((item) =>
      !Object.values(professionalSelfServiceKeys).includes(item.key as string));
  }

  if (fromPendingActivations) {
    return arr.filter(
      (item) =>
        item.key !== professionalSelfServiceKeys.billing,
    );
  }
  return arr;
};
